import { Navigate, Outlet, useLocation, useSearchParams } from 'react-router-dom'
import * as React from 'react'
import * as Sentry from '@sentry/react'
import { useEffect, useMemo, useState } from 'react'
import { PermissionInfo } from '../contexts/permission/PermissionModel'
import { useAppDispatch, useAppSelector } from '../store/hooks'
import NotAuthorizedPage from './NotAuthorizedPage'
import { logout, selectAuthenticatedUser, selectAuthenticatedUserToken } from '../store/auth/authSlice'
import { IntercomWrapper } from './Intercom/IntercomWrapper'
import { readToken, Token } from '../common/token'
import { refreshToken } from '../store/auth/authActions'
import { refreshLock } from '../common/baseQuery'
import { RedirectProgress } from './RedirectProgress/RedirectProgress'
import { getLoginAppBaseUrl } from '../common/companySso'
import { resetFlow } from '../store/mfa/mfaSlice'
import { MFAModal } from '../views/MFA/MFAModal'
import { useShouldShowMFASetupOverlay } from '../views/MFA/useShouldShowMFASetupOverlay'

interface AuthorizedRouteProps {
  allowedTo: Array<PermissionInfo> | PermissionInfo
  checkAuthenticationOnly?: boolean
  children?: JSX.Element
}
export const AuthorizedRoute = ({ allowedTo, checkAuthenticationOnly = false, children }: AuthorizedRouteProps): JSX.Element | null => {
  const user = useAppSelector(selectAuthenticatedUser)
  const userToken = useAppSelector(selectAuthenticatedUserToken)
  const dispatch = useAppDispatch()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const code = searchParams.get('code')

  const redirectUrl = !/logout/.test(window.location.pathname)
    ? `${getLoginAppBaseUrl()}?redirect=${encodeURIComponent(window.location.href)}${code ? `&code=${code}` : ''}`
    : '/'

  useEffect(() => {
    if (userToken) {
      const token = Token.fromSerialized(userToken)
      if (token.isAccessTokenExpired) {
        if (token.isRenewable) {
          refreshLock(() => {
            dispatch(refreshToken({ accountId: token.accountId, refreshToken: token.refreshToken! }))
          }).catch(console.error)
        } else {
          dispatch(logout(location))
          window.location.href = redirectUrl
        }
      }
    } else {
      const token = readToken()
      if (token) {
        refreshLock(() => {
          dispatch(refreshToken({ accountId: token.accountId, refreshToken: token.refreshToken! }))
        }).catch(console.error)
      }
    }
  }, [userToken, dispatch, location, redirectUrl])

  useEffect(() => {
    if (!user) {
      return
    }

    const { id, accountId } = user
    Sentry.setTag('accountId', accountId)
    Sentry.setUser({ id, accountId })

    return () => {
      Sentry.setUser(null)
    }
  }, [user])

  const token = useMemo(() => {
    return userToken ? Token.fromSerialized(userToken) : null
  }, [userToken])

  const shouldShowMFASetupOverlay = useShouldShowMFASetupOverlay(user, token)
  const [showMfaModal, setShowMfaModal] = useState(false)

  const loginMethodChangeRequested = token?.isLoginMethodChangeRequested ?? false

  useEffect(() => {
    if (shouldShowMFASetupOverlay) {
      setShowMfaModal(true)
    }
  }, [shouldShowMFASetupOverlay])

  if (!user) {
    const token = readToken()
    if (token != null) {
      return <RedirectProgress />
    } else {
      window.location.href = redirectUrl
      return null
    }
  }

  // Redirect during render (not in an effect) so the guarded route never mounts: this prevents
  // both the flash of the original route and any data-fetching effects it would fire before the
  // forced sign-up-change redirect.
  if (loginMethodChangeRequested && location.pathname !== '/sign-up-requested') {
    return <Navigate to='/sign-up-requested' replace />
  }

  const scopes = (Array.isArray(allowedTo) ? allowedTo : [allowedTo]).map((perm) => perm.id)
  const adminScopes = scopes.map((scope) => `${scope.split(':')[0]}:admin`)

  const hasScope = user.permissions && user.permissions.some((scope: string) => scopes.concat(adminScopes).includes(scope))

  if (checkAuthenticationOnly || hasScope || token?.isInitialToken) {
    return (
      <IntercomWrapper>
        <>
          {children ? children : <Outlet />}
          {showMfaModal && !token?.isInitialToken ? (
            <MFAModal
              show
              onHide={() => {
                dispatch(resetFlow())
                setShowMfaModal(false)
              }}
              loading={false}
              login={false}
              dismissible={false}
            />
          ) : null}
        </>
      </IntercomWrapper>
    )
  }

  return <NotAuthorizedPage />
}
